/**
 * Payments (docs/PAYMENTS.md): the Stripe prices made from planFor, the subscription items a plan needs, what the
 * owner sees about their plan, and when to ask for a card. Pure: no Stripe calls here (services/api/src/stripe.ts).
 *
 * Owner decisions (2026-09-28/29): Stripe Checkout takes the card; 25 free days that start when the card is added;
 * canceled during the trial = no charge (and the referral is revoked, core referrals.ts). Homes (owner, 2026-10-14): 3 free days
 * (HOME_TRIAL_DAYS), then $18 a month with the AI included; the same card rule.
 * When to ask (Fogg, B = MAP: motivation, ability and a prompt at the same moment): at the Save of a record (owner,
 * 2026-10-10: "they put the data in and then they get an almost there! To put their card in"): the entry is held as the
 * person's draft and "Almost there!" asks for the card (core paywall.ts CARD_TO_SAVE); or right before the next value (the
 * first binder download, the first look at Insights or Health). Never before any value, never on a safety record, the
 * before-asks at most once a day, and after three "Not now"s only a calm banner.
 */
import { AI_PHOTOS_PRICE_CENTS, AI_ROOMCHECK_PRICE_CENTS, BUILDING_PRICE_CENTS, HOME_AI_PRICE_CENTS, HOME_PLAN_PRICE_CENTS, HOME_PRICE_CENTS, HOME_TRIAL_DAYS, LEGACY_HOME_LOOKUP_KEYS, homeOfferLine, PLAN_BANDS, PLAN_BAND_LABEL, TRIAL_DAYS, trialDaysFor, type BusinessPlan, type PlanBand, type TrialStatus } from "./billing.ts";
import type { IsoDate, IsoDateTime, OrgBilling, OrgKind } from "./model.ts";
import { dayIn, prettyDay } from "./referrals.ts";
import { timeZoneOf } from "./states/index.ts";
import { oneLine } from "./emaildesign.ts";
import { CARD_TO_SAVE } from "./paywall.ts";

/* ─────────────── prices (one rule set: planFor, T7) ─────────────── */

/** Bump when a price changes: Stripe prices can't change amount, so a new price gets a new lookup key. */
export const STRIPE_PRICE_VERSION = "v1";
/** The product tax code on every CleanCleaver Stripe product: "Software as a service (SaaS) - business use". The account uses
 *  Managed Payments, which refuses a Checkout line whose product has no eligible tax code (Stripe, 2026-10-05:
 *  https://docs.stripe.com/payments/managed-payments/eligibility). Homes use STRIPE_TAX_CODE_HOME. */
export const STRIPE_TAX_CODE = "txcd_10103001";
/** Homes are personal use: "Software as a service (SaaS) - personal use" (docs.stripe.com/tax/tax-codes, read 2026-10-09). */
export const STRIPE_TAX_CODE_HOME = "txcd_10103000";
/** Checkout's `trial_period_days` for a business: the one 25 (core billing.ts TRIAL_DAYS). A home: HOME_TRIAL_PERIOD_DAYS. */
export const TRIAL_PERIOD_DAYS = TRIAL_DAYS;
/** Checkout's `trial_period_days` for a home: 3 (owner, 2026-10-14; core billing.ts HOME_TRIAL_DAYS). */
export const HOME_TRIAL_PERIOD_DAYS = HOME_TRIAL_DAYS;
/** The free days for this kind of place (the one rule every Checkout, date, banner and email reads). */
export const trialPeriodDaysFor = trialDaysFor;
/**
 * The Stripe prices. "home_plan": the $18 home plan (owner, 2026-10-14), the only home price offered. "home" ($8.99) and "home_ai"
 * ($15): retired, kept so a subscription already on them is read and its AI switch still moves between them.
 */
export type PriceKey = PlanBand | "ai_photos" | "ai_roomcheck" | "home_plan" | "home" | "home_ai";

export function priceLookupKey(k: PriceKey): string {
  // The retired home prices are frozen: their keys never follow STRIPE_PRICE_VERSION (core billing.ts LEGACY_HOME_LOOKUP_KEYS).
  if (k === "home" || k === "home_ai") return LEGACY_HOME_LOOKUP_KEYS[k];
  if (k === "home_plan") return `cc_home_plan_${STRIPE_PRICE_VERSION}`;
  return k === "ai_photos" || k === "ai_roomcheck" ? `cc_addon_${k}_${STRIPE_PRICE_VERSION}` : `cc_band_${k}_${STRIPE_PRICE_VERSION}`;
}

/**
 * `taxCode`: the product's Stripe tax code (business SaaS unless said; homes are personal use). `retired`: never offered to a new
 * plan; kept so the subscriptions already on it are read and synced (never edited: a Stripe price's amount can't change).
 */
export interface StripePriceSpec { key: PriceKey; lookupKey: string; product: string; nickname: string; unitAmount: number; currency: "usd"; interval: "month"; taxCode?: string; retired?: true }

/** Every price the business plans use, from the same constants planFor reads. */
export const STRIPE_PRICES: readonly StripePriceSpec[] = [
  ...PLAN_BANDS.map((b) => ({ key: b.band as PriceKey, lookupKey: priceLookupKey(b.band), product: `CleanCleaver ${b.label} (per location)`, nickname: `${b.label} band, per location, monthly`, unitAmount: b.priceCents, currency: "usd" as const, interval: "month" as const })),
  { key: "building", lookupKey: priceLookupKey("building"), product: `CleanCleaver ${PLAN_BAND_LABEL.building} (per building)`, nickname: "Community building, monthly", unitAmount: BUILDING_PRICE_CENTS, currency: "usd", interval: "month" },
  { key: "ai_photos", lookupKey: priceLookupKey("ai_photos"), product: "CleanCleaver AI add-on: food photos (per person)", nickname: "AI food photos, per counted person, monthly", unitAmount: AI_PHOTOS_PRICE_CENTS, currency: "usd", interval: "month" },
  { key: "ai_roomcheck", lookupKey: priceLookupKey("ai_roomcheck"), product: "CleanCleaver AI add-on: RoomCheck (per person)", nickname: "AI RoomCheck, per counted person, monthly", unitAmount: AI_ROOMCHECK_PRICE_CENTS, currency: "usd", interval: "month" },
  // Homes (owner, 2026-10-14: "3 days free and then $18/mo"): one price per home, AI included, a NEW lookup key (no existing price changes).
  { key: "home_plan", lookupKey: priceLookupKey("home_plan"), product: "CleanCleaver Home plan (per home, AI included)", nickname: "Home plan, AI included, monthly", unitAmount: HOME_PLAN_PRICE_CENTS, currency: "usd", interval: "month", taxCode: STRIPE_TAX_CODE_HOME },
  // Retired (owner, 2026-10-09: "$15 for home with AI. $8.99 without."): only for subscriptions already on them.
  { key: "home", lookupKey: priceLookupKey("home"), product: "CleanCleaver Home (per home)", nickname: "Home, monthly", unitAmount: HOME_PRICE_CENTS, currency: "usd", interval: "month", taxCode: STRIPE_TAX_CODE_HOME, retired: true },
  { key: "home_ai", lookupKey: priceLookupKey("home_ai"), product: "CleanCleaver Home with AI (per home)", nickname: "Home with AI, monthly", unitAmount: HOME_AI_PRICE_CENTS, currency: "usd", interval: "month", taxCode: STRIPE_TAX_CODE_HOME, retired: true },
];

export interface SubscriptionItemSpec { lookupKey: string; quantity: number }

/**
 * The subscription a plan needs: one item per band price with quantity = locations at that band (Stripe allows a
 * price once per subscription), plus the AI add-on with quantity = counted people. Homes (free): none.
 */
export function subscriptionItemsFor(plan: Pick<BusinessPlan, "locations" | "addons" | "free" | "home">): SubscriptionItemSpec[] {
  if (plan.free) return [];
  // A home: one item, quantity 1: the $18 home plan, or the retired price its running subscription is already on.
  if (plan.home) return [{ lookupKey: priceLookupKey(plan.home.legacy ?? "home_plan"), quantity: 1 }];
  const qty = new Map<string, number>();
  for (const l of plan.locations) qty.set(priceLookupKey(l.band), (qty.get(priceLookupKey(l.band)) ?? 0) + 1);
  const people = plan.locations.reduce((s, l) => s + l.people, 0);
  if (plan.addons.aiPhotos && people > 0) qty.set(priceLookupKey("ai_photos"), people);
  if (plan.addons.aiRoomcheck && people > 0) qty.set(priceLookupKey("ai_roomcheck"), people);
  return STRIPE_PRICES.filter((p) => qty.has(p.lookupKey)).map((p) => ({ lookupKey: p.lookupKey, quantity: qty.get(p.lookupKey)! }));
}

/** What those items cost a month (must equal planFor's total: tested). */
export function itemsTotalCents(items: readonly SubscriptionItemSpec[]): number {
  return items.reduce((s, i) => s + (STRIPE_PRICES.find((p) => p.lookupKey === i.lookupKey)?.unitAmount ?? NaN) * i.quantity, 0);
}

/**
 * The changes that turn the stored items into the wanted ones: update a quantity, add a price, delete an item.
 * Empty when nothing changed (so a sync sends nothing).
 */
export function itemChanges(have: Record<string, { id: string; quantity: number }>, want: readonly SubscriptionItemSpec[]): ({ id: string; quantity: number } | { lookupKey: string; quantity: number } | { id: string; deleted: true })[] {
  const out: ({ id: string; quantity: number } | { lookupKey: string; quantity: number } | { id: string; deleted: true })[] = [];
  for (const w of want) {
    const h = have[w.lookupKey];
    if (!h) out.push({ lookupKey: w.lookupKey, quantity: w.quantity });
    else if (h.quantity !== w.quantity) out.push({ id: h.id, quantity: w.quantity });
  }
  for (const [key, h] of Object.entries(have)) if (!want.some((w) => w.lookupKey === key)) out.push({ id: h.id, deleted: true });
  return out;
}

/* ─────────────── what the owner sees ─────────────── */

export const dollarsOf = (cents: number) => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;

/** free: a home while home billing is paused. none: no card yet (the free days haven't started). The rest are Stripe's subscription states. */
export type BillingState = "free" | "none" | "trialing" | "active" | "past_due" | "unpaid" | "canceled";
export type BillingBannerKind = "no_card" | "trial_ending" | "past_due" | "canceled";

export interface BillingView {
  state: BillingState;
  cardOnFile: boolean;
  /** The plan's monthly price now (core planFor), shown next to the status. */
  monthlyCents: number;
  /** The one status line: "Free trial: 18 days left · then $24/month". */
  line: string;
  /** The Start screen's price line: "$24 a month after 25 days. Cancel anytime before and you pay nothing." A home: "3 days free, then $18/month. Cancel anytime." */
  priceLine: string;
  trialDaysLeft: number | null;
  trialEndsOn: IsoDate | null;
  /** The next charge (Utah date), when one is due. */
  nextChargeOn: IsoDate | null;
  cancelAtPeriodEnd: boolean;
  /** May open Checkout (no card yet, or canceled and starting again). */
  canStart: boolean;
  /** Has a Stripe customer: the Portal (card, invoices, cancel) opens. */
  canManage: boolean;
  /** Referral months applied as credit (cents). */
  creditsCents: number;
  /** What always works, whatever the plan says (S3: no walls on safety). */
  alwaysWorks: string;
  banner: { kind: BillingBannerKind; text: string; action: string } | null;
}

/** What a business keeps when its plan isn't running (owner, 2026-10-08: the app locks, records are never held hostage; core paywall.ts). */
export const ALWAYS_WORKS = "Health check-ins and sick reports always work. Your records stay yours: you can always read them, download your binder and export everything.";
/** A banner shows in the last TRIAL_BANNER_DAYS of a business's trial. */
export const TRIAL_BANNER_DAYS = 5;
/** The trial-ending notice goes out this many days before a business's first charge (day 22 of 25). */
export const TRIAL_NOTICE_DAYS = 3;
/**
 * A home's 3 free days (HOME_TRIAL_DAYS): "3 days before" would be the moment the card is added, so the notice and the banner come
 * on the last day instead (the trial's last 24 hours, day 3 of 3), when "ends tomorrow" is news and there is still time to cancel.
 */
export const HOME_TRIAL_NOTICE_DAYS = 1;
export const HOME_TRIAL_BANNER_DAYS = 1;
export const trialNoticeDaysFor = (kind: string | undefined | null): number => (kind === "home" ? HOME_TRIAL_NOTICE_DAYS : TRIAL_NOTICE_DAYS);
export const trialBannerDaysFor = (kind: string | undefined | null): number => (kind === "home" ? HOME_TRIAL_BANNER_DAYS : TRIAL_BANNER_DAYS);
const DAY_MS = 86_400_000;

export function startPriceLine(monthlyCents: number, kind?: OrgKind | null): string {
  if (kind === "home") return homeOfferLine(monthlyCents);
  return `${dollarsOf(monthlyCents)} a month after ${TRIAL_PERIOD_DAYS} days. Cancel anytime before and you pay nothing.`;
}

/**
 * The price line of a plan that starts AGAIN (canceled, or its first card never confirmed after a trial ran): no second free period, so it says
 * what is charged today (owner/security run-23 R23-02; NY GBL 527-a(1)(a), CA 17602(a)(1)). Homes and businesses alike.
 */
export function restartPriceLine(monthlyCents: number): string {
  return `${dollarsOf(monthlyCents)} a month, charged to your card today. Cancel anytime.`;
}

/** Whole days until the trial ends, rounded up (0 once it has ended). */
export function trialDaysLeft(trialEndsAt: IsoDateTime, now: Date): number {
  const ms = Date.parse(trialEndsAt) - now.getTime();
  return Number.isFinite(ms) ? Math.max(0, Math.ceil(ms / DAY_MS)) : 0;
}

/**
 * The plan status an owner sees. `billing` is only what signed Stripe webhooks stored (never the client). A home is
 * free only while home billing is paused; no billing yet is "none" (the free days start with the card).
 */
export function billingView(org: { kind: OrgKind; billing?: OrgBilling; state?: string | null }, monthlyCents: number, now: Date): BillingView {
  const b = org.billing;
  const month = `${dollarsOf(monthlyCents)}/month`;
  const days = trialDaysFor(org.kind);
  const base = { cardOnFile: !!b?.cardOnFile, monthlyCents, priceLine: startPriceLine(monthlyCents, org.kind), cancelAtPeriodEnd: !!b?.stripe?.cancelAtPeriodEnd, canManage: !!b?.stripe?.customerId, creditsCents: b?.stripe?.creditsCents ?? 0, alwaysWorks: ALWAYS_WORKS };
  // A home is free only while home billing is paused (HOMES_BILLING=off gives a $0 plan); otherwise it pays like a business.
  if (org.kind === "home" && monthlyCents === 0) return { ...base, state: "free", line: "Free while we test", priceLine: "Free while we test. No card needed.", trialDaysLeft: null, trialEndsOn: null, nextChargeOn: null, canStart: false, canManage: false, banner: null };
  if (!b) return { ...base, state: "none", line: `Not started · add a card to start your ${days} free days`, trialDaysLeft: null, trialEndsOn: null, nextChargeOn: null, canStart: true, banner: { kind: "no_card", text: "Add a card to start your free days", action: "Add a card" } };
  // Stripe's trial_end and current_period_end are instants (unchanged); the day shown is that instant's day in the business's
  // own state's zone (fix/one-truth: Utah's for every business before; a New York trial ending at 01:00 New York time showed
  // the day before).
  const endsOn = (iso?: string) => (iso && Number.isFinite(Date.parse(iso)) ? dayIn(iso, timeZoneOf(org.state)) : null);
  switch (b.status) {
    case "trialing": {
      const left = b.trialEndsAt ? trialDaysLeft(b.trialEndsAt, now) : null;
      const on = endsOn(b.trialEndsAt);
      const ending = b.stripe?.cancelAtPeriodEnd;
      const line = left === null ? `Free trial · then ${month}` : ending ? `Free trial: ${left <= 1 ? "last day" : `${left} days left`} · ends then, no charge` : left <= 1 ? `Free trial: last day today · then ${month}` : `Free trial: ${left} days left · then ${month}`;
      const banner = left !== null && left <= trialBannerDaysFor(org.kind) && !ending
        ? { kind: "trial_ending" as const, text: left <= 1 ? `Your free trial ends today. Then ${dollarsOf(monthlyCents)} a month.` : `Your free trial ends in ${left} days. Then ${dollarsOf(monthlyCents)} a month.`, action: "Manage billing" }
        : null;
      return { ...base, state: "trialing", line, trialDaysLeft: left, trialEndsOn: on, nextChargeOn: ending ? null : on, canStart: false, banner };
    }
    case "active": {
      const on = endsOn(b.currentPeriodEnd);
      const line = b.stripe?.cancelAtPeriodEnd ? `Active until ${on ? prettyDay(on) : "the end of this month"} · then it ends` : `Active · ${month}${on ? ` · next charge ${prettyDay(on)}` : ""}`;
      return { ...base, state: "active", line, trialDaysLeft: null, trialEndsOn: endsOn(b.trialEndsAt), nextChargeOn: b.stripe?.cancelAtPeriodEnd ? null : on, canStart: false, banner: null };
    }
    case "unpaid":
      return { ...base, state: "unpaid", line: "Payments didn't go through · fix your card to open everything again", trialDaysLeft: null, trialEndsOn: endsOn(b.trialEndsAt), nextChargeOn: null, canStart: false, banner: { kind: "past_due", text: "Your payments didn't go through. Fix your card to open everything again.", action: "Fix your card" } };
    case "incomplete":
      return { ...base, state: "none", line: `Not started · your card still needs a step on Stripe's page`, trialDaysLeft: null, trialEndsOn: null, nextChargeOn: null, canStart: true, banner: { kind: "no_card", text: "Your card still needs a step. Start your plan again.", action: "Add a card" } };
    case "incomplete_expired":
    case "past_due":
      if (b.status === "incomplete_expired") return { ...base, ...(b.trialEndsAt ? { priceLine: restartPriceLine(monthlyCents) } : {}), state: "canceled", line: "Not started · the card wasn't confirmed in time", trialDaysLeft: null, trialEndsOn: null, nextChargeOn: null, canStart: true, banner: { kind: "canceled", text: `Your plan didn't start. ${ALWAYS_WORKS}`, action: "Start again" } };
      return { ...base, state: "past_due", line: "Payment didn't go through · update your card", trialDaysLeft: null, trialEndsOn: endsOn(b.trialEndsAt), nextChargeOn: null, canStart: false, banner: { kind: "past_due", text: "Your last payment didn't go through. Update your card to keep your plan.", action: "Update card" } };
    case "canceled":
      return { ...base, ...(b.trialEndsAt ? { priceLine: restartPriceLine(monthlyCents) } : {}), state: "canceled", line: "Canceled · no more charges", trialDaysLeft: null, trialEndsOn: endsOn(b.trialEndsAt), nextChargeOn: null, canStart: true, banner: { kind: "canceled", text: `Your plan is canceled. ${ALWAYS_WORKS}`, action: "Start again" } };
  }
}

/** Which banner shows where: past-due and no-card everywhere; the trial and canceled notes on Today only (calm). */
export function bannerShows(kind: BillingBannerKind, onToday: boolean): boolean {
  return kind === "past_due" || kind === "no_card" || onToday;
}

/* ─────────────── the renewal terms, said the same way everywhere (docs/legal/EASY-CANCEL.md) ─────────────── */

/** The day the free days end if the card is added now: Stripe's trial_end is now + the place's free days, shown in its zone. */
export function trialEndsOnFrom(now: Date, timeZone: string, kind?: OrgKind | null): IsoDate {
  return dayIn(new Date(now.getTime() + trialDaysFor(kind) * DAY_MS).toISOString(), timeZone);
}

/** Where the plan is managed and canceled on the web: the same two addresses in every disclosure, email and phone screen. */
export const billingLinks = (webAppUrl: string) => ({ manageUrl: `${webAppUrl}/billing`, cancelUrl: `${webAppUrl}/billing/cancel` });

/** How to cancel, in one line: online, two taps, no call (the strictest supported state's rule, docs/legal/EASY-CANCEL.md). */
export const HOW_TO_CANCEL = "Cancel online any time, in two taps: Plan and billing → Cancel plan. No call, no chat, no waiting.";

/**
 * The automatic-renewal terms (New York GBL §527-a, California B&P §17602: price, when the charge starts, that it repeats until
 * canceled, and how to cancel), as short lines. Shown before the card is added (web StartTrialCard), next to Stripe's pay
 * button (Checkout custom_text), in the acknowledgement email and on the phone. `trialEndsOn` null: no free days (a plan
 * starting again), so the first charge is today.
 */
export function renewalTerms(o: { monthlyCents: number; trialEndsOn: IsoDate | null; cancelUrl?: string; kind?: OrgKind | null }): string[] {
  const price = dollarsOf(o.monthlyCents);
  const on = o.trialEndsOn ? prettyDay(o.trialEndsOn) : null;
  return [
    on
      ? `Free until ${on}. Then ${price} a month, charged to your card on ${on} and on that day each month, until you cancel.`
      : `${price} a month, charged to your card today and on this day each month, until you cancel.`,
    o.kind === "home" ? "One price for your home, however many people live there. If it ever changes, we tell you first." : "The price only changes when you add or remove people or locations, and we say the new price first.",
    `${HOW_TO_CANCEL}${o.cancelUrl ? ` ${o.cancelUrl}` : ""}`,
    ...(on ? [`Cancel before ${on} and you pay nothing.`] : ["Cancel and the plan runs to the end of the month you paid for; nothing more is charged."]),
  ];
}

/**
 * What a Checkout made now would sell, in words (services/api billing.ts checkoutSummaryFor; GET /orgs/:orgId/billing `checkout`
 * while canStart). The iPhone app's Checkout screen shows it as is: no price is computed or hard-coded on the phone.
 */
export interface CheckoutSummary {
  business: string;
  locations: { name: string; band: string; people: number; priceCents: number; line: string }[];
  addons: string[];
  total: string;
  monthlyCents: number;
  freeDays: number;
  /** The first charge day (the business's own zone), or null: no free days, charged today. */
  trialEndsOn: string | null;
  terms: string[];
}

/** Next to Stripe's pay button (Checkout `custom_text.submit.message`, 1,200 characters at most). */
export function checkoutSubmitText(o: { monthlyCents: number; trialEndsOn: IsoDate | null; cancelUrl: string; kind?: OrgKind | null }): string {
  return renewalTerms(o).join(" ").slice(0, 1200);
}

/**
 * The acknowledgement email, right after the card is added (California B&P §17602(a)(3): the terms, the cancellation policy and
 * how to cancel, in a form the person can keep; New York GBL §527-a(2)(c) the same). Sent once per subscription.
 */
export function checkoutAckEmail(businessName: string, o: { monthlyCents: number; trialEndsOn: IsoDate | null; kind?: OrgKind | null }, links: { manageUrl: string; cancelUrl: string }): BillingEmail {
  const lines = renewalTerms({ ...o, cancelUrl: links.cancelUrl });
  return {
    subject: o.trialEndsOn ? `Your ${trialDaysFor(o.kind)} free days at CleanCleaver have started` : "Your CleanCleaver plan has started",
    text: `Hi,\n\n${oneLine(businessName)} is set up on CleanCleaver. Here is what you agreed to, in writing, so you can keep it:\n\n${lines.join("\n")}\n\nPlan and billing: ${links.manageUrl}\n\n${ALWAYS_WORKS}\n`,
    button: { label: "Plan and billing", url: links.manageUrl },
  };
}

/** A billing email: the words, and the ONE button (core noteEmail renders only this one; a text line never becomes a button). */
export interface BillingEmail { subject: string; text: string; button: { label: string; url: string } | null }

/** The trial-ending notice (email + in-app), sent once, trialNoticeDaysFor(kind) before the first charge (a business 3, a home 1). `links`: the direct way to cancel online. */
export function trialEndingNotice(businessName: string, monthlyCents: number, trialEndsOn: IsoDate, links?: { manageUrl: string; cancelUrl: string }): BillingEmail {
  const on = prettyDay(trialEndsOn);
  const how = links ? `To stop, cancel online before ${on}, in two taps: ${links.cancelUrl} (no call, no chat). You won't be charged.` : `To stop, open CleanCleaver → Plan and billing → Cancel plan, and cancel before ${on}. You won't be charged.`;
  return {
    subject: `Your CleanCleaver free trial ends ${on}`,
    button: links ? { label: "Plan and billing", url: links.manageUrl } : null,
    text: `Hi,\n\nThe free trial for ${oneLine(businessName)} ends on ${on}. Then it's ${dollarsOf(monthlyCents)} a month on the card you added, each month until you cancel.\n\nTo keep going, do nothing. ${how}\n\n${links ? `Plan and billing: ${links.manageUrl}\n\n` : ""}${ALWAYS_WORKS}\n`,
  };
}
/**
 * "Your card didn't go through" (owner, 2026-10-12: "this is the only email because it is extrinsic to the app. It is needed for raw
 * functioning"). Always sent (core notifyprefs.ts N5: not a notification, no switch), once per failed invoice attempt, to the billing
 * contacts. What happened, how many days are left before the plan locks (PLAN_GRACE_DAYS), and one button: Fix your card.
 */
export function cardFailedEmail(businessName: string, o: { daysLeft: number; locksOn: IsoDate }, manageUrl: string): BillingEmail {
  const days = Math.max(0, Math.round(o.daysLeft));
  const when = days > 0 ? `Everything keeps working for ${days} more day${days === 1 ? "" : "s"}, until ${prettyDay(o.locksOn)}.` : "The grace days are over.";
  return {
    subject: `Your card didn't go through for ${oneLine(businessName)}`,
    button: { label: "Fix your card", url: manageUrl },
    text: `Hi,\n\nThe payment for ${oneLine(businessName)}'s CleanCleaver plan didn't go through, so the plan is on hold.\n\n${when} Then the plan locks: new records wait until the card is fixed.\n\nFix your card: ${manageUrl}\n\n${ALWAYS_WORKS}\n`,
  };
}

/**
 * Due now? A trialing plan whose trial ends within its notice days (trialNoticeDaysFor: a business 3 of 25, a home the last 1 of 3)
 * and hasn't been told. `kind` absent reads as a business.
 */
export function trialNoticeDue(b: OrgBilling | undefined, now: Date, kind?: OrgKind | null): boolean {
  if (!b || b.status !== "trialing" || !b.trialEndsAt || b.stripe?.cancelAtPeriodEnd) return false;
  const ms = Date.parse(b.trialEndsAt) - now.getTime();
  return ms > 0 && ms <= trialNoticeDaysFor(kind) * DAY_MS;
}

/* ─────────────── when to ask for the card (B = MAP) ─────────────── */

/** save: a record's Save was held for the card (always shown, it says where the entry went). The others: right before the first binder / Insights / Health. */
export type CardPromptTrigger = "save" | "binder" | "insights" | "health";
/** The before-asks the core rule decides (cardPromptDecision); "save" is shown every time a save is held. */
export const CARD_PROMPT_TRIGGERS: readonly CardPromptTrigger[] = ["binder", "insights", "health"];
export const CARD_PROMPT_MAX_NOT_NOW = 3;

/** What this person's browser remembers about the prompt for one business. */
export interface CardPromptMemory { notNow: number; lastShownDay?: IsoDate; shown: CardPromptTrigger[] }
export const NO_PROMPT_MEMORY: CardPromptMemory = { notNow: 0, shown: [] };

export interface CardPromptInput {
  state: BillingState;
  /** Owner or admin (the only people who can add a card). */
  canPay: boolean;
  memory: CardPromptMemory;
  today: IsoDate;
  trigger: CardPromptTrigger;
}

/**
 * Show the card prompt now? Only when the 25 days haven't started, only to an owner or admin, each moment once, at
 * most once a day, never after three "Not now"s (then the banner is enough).
 */
export function cardPromptDecision(i: CardPromptInput): boolean {
  if (i.state !== "none" || !i.canPay) return false;
  if (i.memory.notNow >= CARD_PROMPT_MAX_NOT_NOW) return false;
  if (i.memory.lastShownDay === i.today) return false;
  return !i.memory.shown.includes(i.trigger);
}
export function promptShown(m: CardPromptMemory, trigger: CardPromptTrigger, today: IsoDate): CardPromptMemory {
  return { ...m, lastShownDay: today, shown: m.shown.includes(trigger) ? m.shown : [...m.shown, trigger] };
}
export function promptNotNow(m: CardPromptMemory): CardPromptMemory {
  return { ...m, notNow: m.notNow + 1 };
}
/** The calm "Add a card" banner waits until value was shown (the first prompt), so no one is asked before anything worked. */
export function noCardBannerShows(m: CardPromptMemory): boolean {
  return m.shown.length > 0 || m.notNow > 0;
}

/** The prompt's words. "save": the held Save ("Almost there!"); otherwise it comes before the next value. */
export function cardPromptWords(trigger: CardPromptTrigger, monthlyCents: number, kind?: OrgKind | null): { title: string; lede: string; action: string; price: string; later: string } {
  const before: Record<Exclude<CardPromptTrigger, "save">, string> = {
    binder: "Your binder is ready to download.",
    insights: "Your insights are ready.",
    health: "Your health check is ready.",
  };
  return {
    title: trigger === "save" ? CARD_TO_SAVE.title : before[trigger],
    lede: trigger === "save" ? CARD_TO_SAVE.body : `Keep it going: start your ${trialDaysFor(kind)} free days.`,
    action: CARD_TO_SAVE.addCard,
    price: startPriceLine(monthlyCents, kind),
    later: "Not now",
  };
}

/**
 * The trial for every plan screen (web Settings, phone Locations, GET /orgs/:orgId/plan), in core billing.ts
 * TrialStatus shape so trialLine words stay the same. The ONLY trial clock: it reads Stripe's `trial_end`, which the
 * signed `customer.subscription.*` webhook stored as `billing.trialEndsAt` (services/api/src/billing.ts). Stripe starts
 * that trial when Checkout takes the card (docs.stripe.com/payments/checkout/free-trials, `trial_period_days`), so
 * the free days run from the card, never from the business's creation date. Null before a card (no clock runs, however
 * old the business). `days`: the place's trial length (a home's 3, a business's 25).
 */
export function trialFromBilling(org: { kind: OrgKind; billing?: OrgBilling; state?: string | null }, now: Date): TrialStatus | null {
  const b = org.billing;
  if (!b?.trialEndsAt || !Number.isFinite(Date.parse(b.trialEndsAt))) return null;
  const daysLeft = b.status === "trialing" ? trialDaysLeft(b.trialEndsAt, now) : 0;
  return { inTrial: daysLeft > 0, endsOn: dayIn(b.trialEndsAt, timeZoneOf(org.state)), daysLeft, days: trialDaysFor(org.kind) };
}
